import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { EnterpriseFooter } from "@/pages/enterprise/Enterprise";
import { ArrowRight, TrendingUp, Target, Users, Layers, DollarSign, Award } from "lucide-react";

export default function Investors() {
  const [stats, setStats] = useState({ early_access_members: 0, workspaces: 0, records_created: 0, sops_generated: 0, public_demos_run: 0 });

  useEffect(() => {
    api.get("/stats/public").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-14 flex items-center justify-between">
          <Link to="/" className="font-display font-black text-lg tracking-tight">
            Rule<span className="text-primary">AI</span><span className="text-muted-foreground">.systems</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <Link to="/enterprise" className="hover:text-primary">Enterprise</Link>
            <Link to="/security" className="hover:text-primary">Security</Link>
            <Link to="/about" className="hover:text-primary">Company</Link>
            <Link to="/investors" className="text-primary font-medium">Investors</Link>
            <a href="mailto:invest@ruleai.systems">
              <Button size="sm" className="rounded-sm bg-primary hover:bg-primary/90" data-testid="investors-nav-cta">Request pitch</Button>
            </a>
          </nav>
        </div>
      </header>

      {/* Cover */}
      <section className="border-b border-border">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="overline text-primary mb-4">/ INVESTOR ONE-PAGER · CONFIDENTIAL</div>
          <h1 className="font-display font-black text-4xl md:text-6xl tracking-tight leading-[1.05]" data-testid="investors-hero">
            RuleAI.Systems
          </h1>
          <p className="mt-3 text-xl text-muted-foreground max-w-3xl">
            The AI-native Business Operating System for mid-market manufacturing and industrial operators.
          </p>
          <div className="mt-8 grid md:grid-cols-2 gap-6">
            <div className="border border-border bg-card p-6 rounded-sm">
              <div className="overline text-muted-foreground">Category</div>
              <div className="font-display font-bold text-lg mt-2">Vertical AI SaaS · Industrial Operations</div>
            </div>
            <div className="border border-border bg-card p-6 rounded-sm">
              <div className="overline text-muted-foreground">Stage &amp; ask</div>
              <div className="font-display font-bold text-lg mt-2">Pre-seed · $100K–$500K</div>
            </div>
          </div>
        </div>
      </section>

      {/* The problem */}
      <Section overline="/ 01 PROBLEM" title="Mid-market industrial operators run their business across 8+ disconnected tools.">
        <p className="text-muted-foreground leading-relaxed">
          A typical 50–500 employee manufacturer stitches together Notion (SOPs), Trainual (training), Monday (workflows), MasterControl or Greenlight Guru (quality), BambooHR (HR), Canva (marketing), Miro (process maps), and ChatGPT Team (AI). Average cost: <strong className="text-foreground">$1,340/month</strong>. Institutional knowledge is fragmented. Audit prep takes weeks. Nothing talks to anything else.
        </p>
      </Section>

      {/* Solution */}
      <Section overline="/ 02 SOLUTION" title="One AI-native workspace that replaces the eight-tool stack." dark>
        <p className="text-zinc-400 leading-relaxed">
          RuleAI unifies SOPs, quality management (CAPA, NCR, audits), HR, ops workflows, marketing, and analytics in one workspace. Every module shares a Claude Sonnet 4.6 AI engine. Founding customers save an estimated <strong className="text-white">$16,080 annually</strong> on tool consolidation alone, before productivity gains.
        </p>
        <div className="mt-6 grid md:grid-cols-3 gap-3">
          {[
            { k: "88%", v: "Faster SOP authoring" },
            { k: "8→1", v: "Tools consolidated" },
            { k: "60s", v: "Workspace provisioning" },
          ].map((m) => (
            <div key={m.k} className="border border-zinc-800 bg-zinc-900/60 p-5 rounded-sm">
              <div className="font-display font-black text-3xl text-accent">{m.k}</div>
              <div className="overline text-zinc-500 mt-2">{m.v}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Market */}
      <Section overline="/ 03 MARKET" title="A large, underserved, and rapidly digitizing sector.">
        <div className="grid md:grid-cols-3 gap-4">
          <MarketBox size="TAM" value="$47B" desc="Global operations management + quality software · Gartner, MarketsAndMarkets 2025 estimates" />
          <MarketBox size="SAM" value="$8.2B" desc="US mid-market (50–500 employee) manufacturing, converting, packaging, and industrial services" />
          <MarketBox size="SOM (5-yr)" value="$120M" desc="Target 3,000 workspaces × $40k ACV blended (SMB + Enterprise)" />
        </div>
        <p className="text-muted-foreground text-sm mt-6 leading-relaxed">
          The mid-market industrial segment (10–500 employees) is chronically underserved by both consumer-grade tools (Notion, Trainual) and enterprise platforms (SAP, Oracle). Founders identified this gap through direct experience on manufacturing floors, in quality audits, and inside process improvement engagements.
        </p>
      </Section>

      {/* Traction */}
      <Section overline="/ 04 TRACTION" title="Real customers, real usage, real feedback loop." dark>
        <p className="text-zinc-400 leading-relaxed mb-6">
          The platform is live in private beta at <a href="https://ruleai.systems" className="text-accent hover:underline">ruleai.systems</a>. Every metric below updates in real time from our production database.
        </p>
        <div className="grid md:grid-cols-4 gap-3">
          <TractionBox k={stats.early_access_members} v="Beta members" />
          <TractionBox k={stats.workspaces} v="Active workspaces" />
          <TractionBox k={stats.sops_generated} v="AI SOPs generated" />
          <TractionBox k={stats.records_created} v="Operational records" />
        </div>
        <p className="text-xs font-mono-plex uppercase tracking-widest text-zinc-500 mt-4">
          ◆ Live counts &middot; updated on page load
        </p>
      </Section>

      {/* Business model */}
      <Section overline="/ 05 BUSINESS MODEL" title="Predictable, high-margin SaaS with clear expansion motion.">
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <ModelCard k="Primary revenue" v="Per-seat monthly SaaS · $19–$79/seat blended" />
          <ModelCard k="Expansion" v="Enterprise volume tiers, dedicated instances, custom AI integrations" />
          <ModelCard k="Secondary revenue" v="Manufacturing Excellence Library (premium templates, AI customization)" />
          <ModelCard k="Gross margin target" v="82–88% (software + LLM pass-through)" />
        </div>
        <div className="border border-border bg-card p-6 rounded-sm">
          <div className="overline text-primary mb-3">Ideal Customer Profile (ICP)</div>
          <ul className="space-y-1.5 text-sm">
            {[
              "50–500 employee industrial operators (manufacturing, converting, packaging, warehousing)",
              "Regulated sectors: ISO 9001 · IATF 16949 · ISO 13485 · FDA · AS9100",
              "Buying committee: COO / VP Operations · Head of Quality · Continuous Improvement Manager",
              "Current spend: $12k–$50k/year on the tool stack we replace",
            ].map((row) => (
              <li key={row} className="flex items-start gap-2"><span className="text-primary">◆</span> {row}</li>
            ))}
          </ul>
        </div>
      </Section>

      {/* Team */}
      <Section overline="/ 06 TEAM" title="Founders with lived operational domain expertise." dark>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="border border-zinc-800 bg-zinc-900/60 p-6 rounded-sm">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full overflow-hidden bg-zinc-800 border border-zinc-700 shrink-0">
                <img src="/founder/scott-poore-jr.webp" alt="Scott Poore Jr." className="w-full h-full object-cover" />
              </div>
              <div>
                <div className="font-display font-bold text-lg">Scott Poore Jr.</div>
                <div className="overline text-zinc-500">Founder &amp; CEO</div>
              </div>
            </div>
            <p className="text-sm text-zinc-400 mt-4 leading-relaxed">
              Years of hands-on experience in manufacturing floors, quality departments, and industrial process improvement engagements. Built RuleAI to solve the systems problem he lived every day.
            </p>
          </div>
          <div className="border border-zinc-800 bg-zinc-900/60 p-6 rounded-sm">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full overflow-hidden bg-zinc-800 border border-zinc-700 shrink-0">
                <img src="/founder/heather-eccleston.jpg" alt="Heather Nicole Eccleston" className="w-full h-full object-cover" />
              </div>
              <div>
                <div className="font-display font-bold text-lg">Heather Nicole Eccleston</div>
                <div className="overline text-zinc-500">Co-founder</div>
              </div>
            </div>
            <p className="text-sm text-zinc-400 mt-4 leading-relaxed">
              Partners on go-to-market, customer success, and product strategy. Focused on translating founding customer feedback into an enterprise-ready platform.
            </p>
          </div>
        </div>
      </Section>

      {/* Use of funds */}
      <Section overline="/ 07 USE OF FUNDS" title="Deployed capital, not spent capital.">
        <div className="grid md:grid-cols-3 gap-3 mb-6">
          <UseCard pct="40%" title="Engineering" desc="Depth in Ink & Print vertical, integrations, mobile app, SOC 2 readiness" />
          <UseCard pct="35%" title="Go-to-market" desc="Founding-member outreach, paid acquisition (Meta / Google), industry events" />
          <UseCard pct="25%" title="Ops & Compliance" desc="Legal, security certifications, insurance, financial controls, first hire" />
        </div>
        <div className="border border-border bg-card p-6 rounded-sm">
          <div className="overline text-primary mb-3">12-month milestones</div>
          <ul className="space-y-1.5 text-sm">
            {[
              "Convert 25% of founding beta members to paying (target: 125 paying workspaces)",
              "Close 3 mid-market enterprise agreements ($40k+ ACV)",
              "Ship 5 additional vertical modules (Manufacturing Excellence Library flywheel)",
              "SOC 2 Type I engagement kickoff",
              "First operations / customer success hire",
            ].map((row) => (
              <li key={row} className="flex items-start gap-2"><span className="text-primary">◆</span> {row}</li>
            ))}
          </ul>
        </div>
      </Section>

      {/* CTA */}
      <section className="border-t border-border bg-zinc-50">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16 text-center">
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight">
            Request the full pitch deck.
          </h2>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
            The founders review every investor introduction personally. Request the confidential deck, financial model, and a 30-minute call.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a href="mailto:invest@ruleai.systems?subject=Investor%20intro%20-%20RuleAI.Systems">
              <Button size="lg" className="rounded-sm bg-primary hover:bg-primary/90 h-12 px-8" data-testid="investors-cta-email">
                invest@ruleai.systems <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <Link to="/enterprise">
              <Button size="lg" variant="outline" className="rounded-sm h-12 px-8">
                Enterprise overview
              </Button>
            </Link>
          </div>
          <p className="mt-6 text-[10px] font-mono-plex uppercase tracking-widest text-muted-foreground">
            ◉ This page is confidential and intended only for authorized recipients.
          </p>
        </div>
      </section>

      <EnterpriseFooter />
    </div>
  );
}

function Section({ overline, title, children, dark = false }) {
  return (
    <section className={`border-b border-border ${dark ? "bg-zinc-950 text-white" : ""}`}>
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14 lg:py-16">
        <div className={`overline ${dark ? "text-accent" : "text-primary"} mb-3`}>{overline}</div>
        <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight mb-6 max-w-3xl">{title}</h2>
        {children}
      </div>
    </section>
  );
}

function MarketBox({ size, value, desc }) {
  return (
    <div className="border border-border bg-card p-6 rounded-sm">
      <div className="overline text-muted-foreground">{size}</div>
      <div className="font-display font-black text-4xl mt-1 text-primary">{value}</div>
      <div className="text-xs text-muted-foreground mt-3 leading-relaxed">{desc}</div>
    </div>
  );
}

function TractionBox({ k, v }) {
  return (
    <div className="border border-zinc-800 bg-zinc-900/60 p-5 rounded-sm">
      <div className="font-display font-black text-3xl text-accent">{Number(k || 0).toLocaleString()}</div>
      <div className="overline text-zinc-500 mt-2">{v}</div>
    </div>
  );
}

function ModelCard({ k, v }) {
  return (
    <div className="border border-border bg-card p-5 rounded-sm">
      <div className="overline text-muted-foreground">{k}</div>
      <div className="font-medium text-sm mt-2">{v}</div>
    </div>
  );
}

function UseCard({ pct, title, desc }) {
  return (
    <div className="border border-border bg-card p-5 rounded-sm">
      <div className="font-display font-black text-3xl text-primary">{pct}</div>
      <div className="font-display font-bold text-base mt-2">{title}</div>
      <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{desc}</p>
    </div>
  );
}
